import ApiClient from "@/domain/core/utils/apiClient";
import client_core from "@/services/clients/client_core";
import {MultipleFacesError} from "@/domain/core/exception/exceptions";
import {mapFilters} from "@/domain/photoSearch/service";

const client = new ApiClient(client_core)

const endpoints = {
    config: '/dashboard/v2/photo-archive/{id}/config/',
    tag_definitions: '/dashboard/v2/photo-archive/{id}/tag-definitions/',
    media_detail: '/dashboard/v2/photo-archive/{id}/media/{media_id}/detail/',
    media_metadata: '/dashboard/v2/photo-archive/{id}/media/{media_id}/metadata/',
    media_stats: '/dashboard/v2/photo-search/{id}/media_stats/',
    photos_filter: '/dashboard/v2/photo-search/{id}/filter/',
    download: '/dashboard/v2/photo-search/{id}/download/',
    download_single: '/dashboard/v2/photo-search/{id}/download_single/',
    face: '/dashboard/v2/photo-archive/{id}/face/{face_id}/',
    face_tags: '/dashboard/v2/photo-archive/{id}/face/{face_id}/tags/',
    media_tags: '/dashboard/v2/photo-archive/{id}/media/{media_id}/tags/',
    cluster: '/dashboard/v2/photo-archive/{id}/cluster/{cluster_id}/',
    cluster_tags: '/dashboard/v2/photo-archive/{id}/cluster/{cluster_id}/tags/',
    cluster_link_picacode: '/dashboard/v2/photo-archive/{id}/cluster/{cluster_id}/link_picacode/',
    cluster_unlink_picacode: '/dashboard/v2/photo-archive/{id}/cluster/{cluster_id}/unlink_picacode/',
    identify_person: '/dashboard/v2/photo-archive/{id}/identify/from-image/',
    identify_person_by_bbox: '/dashboard/v2/photo-archive/{id}/identify/by-bbox/',
}

const serviceExport = {
    /**
     * Fetch archive config (archive_settings with patterns)
     */
    async fetchConfig({pica_service_id}) {
        return await client.get(endpoints.config
            .replace('{id}', pica_service_id))
    },

    /**
     * Fetch tag definitions for Photo Archive
     */
    async fetchTagDefinitions({pica_service_id}) {
        const response = await client.get(endpoints.tag_definitions
            .replace('{id}', pica_service_id))
        return response.definitions || []
    },

    /**
     * Fetch detailed information about a specific photo including faces
     */
    async fetchPhotoDetail({pica_service_id}, photo_id) {
        return await client.get(endpoints.media_detail
            .replace('{id}', pica_service_id)
            .replace('{media_id}', photo_id))
    },

    /**
     * Fetch EXIF metadata for a specific photo (camera info, etc.)
     * This is a separate call as it requires S3 access (~100-150ms)
     */
    async fetchPhotoMetadata({pica_service_id}, photo_id) {
        return await client.get(endpoints.media_metadata
            .replace('{id}', pica_service_id)
            .replace('{media_id}', photo_id))
    },

    /**
     * Fetch media stats (total count and optionally position)
     * @param {Object} context - {pica_service_id, gallery_id}
     * @param {Object} options - {filters, photo_id, sorting}
     * @returns {Promise<{total: number, position?: number, photo_id?: number}>}
     */
    async fetchMediaStats({pica_service_id, gallery_id}, {filters = {}, photo_id = null, sorting = '-u'} = {}) {
        const queryParams = []
        if (gallery_id) {
            queryParams.push(`gallery_id=${gallery_id}`)
        }
        queryParams.push(`sorting=${sorting}`)
        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : ''

        const body = {filters: mapFilters(filters)}
        if (photo_id) {
            body.photo_id = photo_id
        }

        return await client.post(
            endpoints.media_stats.replace('{id}', pica_service_id) + queryString,
            body
        )
    },

    /**
     * Fetch photos with generic filters (face_id, cluster_id, etc.)
     * @param {Object} context - {pica_service_id, gallery_id}
     * @param {Object} filters - Filter object (cluster_id, photographer_id, etc.)
     * @param {Object} options - {cursor_after_id, cursor_before_id, include_cursor, sorting} for pagination
     *                          include_cursor: true includes the photo at cursor position in the response
     */
    async fetchPhotosForFilter({pica_service_id, gallery_id}, filters, options = {}) {
        const body = {
            filters: mapFilters(filters),
            selected: []
        }

        // Add cursor IDs for pagination
        if (options.cursor_after_id) {
            body.cursor_after_id = options.cursor_after_id
        }
        if (options.cursor_before_id) {
            body.cursor_before_id = options.cursor_before_id
        }
        // include_cursor: true includes the photo at cursor position in the response
        if (options.include_cursor) {
            body.include_cursor = true
        }

        // Build query params
        const queryParams = []
        if (gallery_id) {
            queryParams.push(`gallery_id=${gallery_id}`)
        }
        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : ''
        const sorting = options.sorting || '-u'

        const url = endpoints.photos_filter.replace('{id}', pica_service_id) + queryString
        return await client.post(url, body, {sorting})
    },

    /**
     * Delete a face from a photo
     */
    async deleteFace({pica_service_id}, face_id) {
        return await client.delete(endpoints.face
            .replace('{id}', pica_service_id)
            .replace('{face_id}', face_id))
    },

    /**
     * Update face tags
     */
    async updateFaceTags({pica_service_id}, face_id, tags) {
        return await client.put(endpoints.face_tags
            .replace('{id}', pica_service_id)
            .replace('{face_id}', face_id), {tags})
    },

    /**
     * Update media tags
     */
    async updateMediaTags({pica_service_id}, media_id, tags) {
        return await client.put(endpoints.media_tags
            .replace('{id}', pica_service_id)
            .replace('{media_id}', media_id), {tags})
    },

    /**
     * Update cluster tags
     */
    async updateClusterTags({pica_service_id}, cluster_id, tags) {
        return await client.put(endpoints.cluster_tags
            .replace('{id}', pica_service_id)
            .replace('{cluster_id}', cluster_id), {tags})
    },

    /**
     * Delete a cluster
     */
    async deleteCluster({pica_service_id}, cluster_id) {
        return await client.delete(endpoints.cluster
            .replace('{id}', pica_service_id)
            .replace('{cluster_id}', cluster_id))
    },

    /**
     * Link pica code to a cluster
     */
    async linkClusterPicaCode({pica_service_id}, cluster_id, pica_code) {
        return await client.post(endpoints.cluster_link_picacode
            .replace('{id}', pica_service_id)
            .replace('{cluster_id}', cluster_id), {picacode: pica_code})
    },

    /**
     * Unlink pica code from a cluster
     */
    async unlinkClusterPicaCode({pica_service_id}, cluster_id) {
        return await client.post(endpoints.cluster_unlink_picacode
            .replace('{id}', pica_service_id)
            .replace('{cluster_id}', cluster_id))
    },

    /**
     * Identify person in uploaded image
     * @param {Object} context - {pica_service_id}
     * @param {File|Blob} imageFile - Image file containing person to identify
     * @param {Number} minConfidence - Minimum confidence threshold (default 0.95)
     * @returns {Promise<Object>} Response with clusters array representing identified persons
     * @throws {MultipleFacesError} If multiple faces detected with faces array
     */
    async identifyPerson({pica_service_id}, imageFile, minConfidence = 0.95) {
        const formData = new FormData()
        formData.append('image', imageFile)
        formData.append('min_confidence', minConfidence)

        try {
            const response = await client.axios.post(
                endpoints.identify_person.replace('{id}', pica_service_id),
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            )
            return response.data // {clusters: [...]}
        } catch (error) {
            // Handle multiple faces detected error
            if (error.response?.data?.code === 'multiple_faces_detected') {
                const faces = error.response.data.faces || []
                throw new MultipleFacesError(
                    error.response.config.url,
                    error.response.status,
                    error.response.data.detail,
                    faces
                )
            }
            // Re-throw other errors
            throw error
        }
    },

    /**
     * Identify person by specific bounding box (after multiple faces detected)
     * @param {Object} params - Parameters
     * @param {number} params.pica_service_id - Pica service ID
     * @param {File} imageFile - Same image file from first request
     * @param {Object} bbox - Bounding box with normalized coordinates (0-1)
     * @param {number} bbox.left - Left position (0-1)
     * @param {number} bbox.top - Top position (0-1)
     * @param {number} bbox.width - Width (0-1)
     * @param {number} bbox.height - Height (0-1)
     * @returns {Promise<{clusters: Array}>} Cluster matches
     */
    async identifyPersonByBbox({pica_service_id}, imageFile, bbox) {
        const formData = new FormData()
        formData.append('image', imageFile)
        formData.append('bbox', JSON.stringify(bbox))

        const response = await client.axios.post(
            endpoints.identify_person_by_bbox.replace('{id}', pica_service_id),
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' }
            }
        )
        return response.data // {clusters: [...]}
    },

    /**
     * Download photos with filters
     * @param {Object} context - {pica_service_id, gallery_id}
     * @param {Object} filters - Filters to apply (cluster_id, etc.)
     * @param {string|null} email - Email for large downloads
     * @param {boolean} single_file - Use single file download endpoint
     * @returns {Promise<Object>} Download response
     */
    async downloadPhotos({pica_service_id, gallery_id}, filters, email = null, single_file = false, page = null, limit = null) {
        const queryParams = []
        if (gallery_id) {
            queryParams.push(`gallery_id=${gallery_id}`)
        }
        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : ''

        // selected is an array of filter objects, e.g. [{cluster_id: 187}]
        const selected = Object.keys(filters).length > 0 ? [filters] : []

        const payload = {
            selected,
            email
        }

        if (page !== null) payload.page = page
        if (limit !== null) payload.limit = limit

        const endpoint = single_file ? endpoints.download_single : endpoints.download
        return await client.post(
            endpoint.replace('{id}', pica_service_id) + queryString,
            payload,
            {},
            'download'
        )
    }
}

export default serviceExport